import { vuexConverter } from '@/utilities/vuex'

import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'

import { RESOURCETYPES } from '@/store/constants'
import { SHARED_NAMESPACE } from '@/store/shared'

const SharedMapper = vuexConverter(SHARED_NAMESPACE)
const permissionEnabledGetter = SharedMapper.mapGetter('permissionEnabled')

export const UserPermissionsActions = {
  changeUserScope: async ({ commit, rootGetters }) => {
    const user = rootGetters.currentUser

    return axiosRequest.patch(`/users/${user.id}/change_scope`).
      then(({ data }) => commit('setCurrentUser', data, { root: true })).
      catch(handleError)
  },

  checkUserPermission: async (_, { userId, resourceType, resourceId }) => {
    return axiosRequest.get(`/users/${userId}/teams/for_resource`, {
      params: { resource_type: resourceType, resource_id: resourceId },
    }).then(({ data }) => data).catch(handleError)
  },

}

export const UserPermissionsGetter = {
  // This should be changed when we migrate user to the correct namespace in the store
  can: (_, __, ___, rootGetters) => permission => {
    if (rootGetters[permissionEnabledGetter] === false) return true
    if (!rootGetters.currentUser?.permissions) return false

    return rootGetters.currentUser?.permissions[permission] || false
  },

  canToResource: (_, getters, ___, rootGetters) => (permission, resource, id) => {
    if (rootGetters[permissionEnabledGetter] === false) return true
    if (!rootGetters.currentUser?.permissions) return false

    if (!permission) throw Error('Permission is required')
    if (!resource) throw Error('Resource is required')

    const currentUser = rootGetters.currentUser
    const globalScope = currentUser.scope_is_global
    const permissionsPerTeam = currentUser.permissions_per_team || {}
    const platformPermissions = currentUser.platform_role?.permissions || []
    const teams = currentUser.teams || []
    const userGlobalTeam = getUserGlobalTeam(teams)

    if (!globalScope) return getters.can(permission)

    const teamsWithAccess = teams.filter(team => {
      const resourceIds = resourceIdsFromTeamByResourceType(team, resource)
      return resourceIds.includes(id)
    }) || []

    // check if the permissions exists in the global team or in any of the teams with access
    if (
      userGlobalTeam &&
      permissionsPerTeam[userGlobalTeam.id] &&
      permissionsPerTeam[userGlobalTeam.id][permission]
    ) return true

    for (const team of teamsWithAccess) {
      if (
        permissionsPerTeam[team.id] &&
        permissionsPerTeam[team.id][permission]
      ) return true
    }

    return platformPermissions.find(p => p.identifier === permission) !== undefined
  },

  canToTeam: (_, getters, ___, rootGetters) => (permission, teamId) => {
    if (rootGetters[permissionEnabledGetter] === false) return true
    if (!rootGetters.currentUser?.permissions) return false

    const currentUser = rootGetters.currentUser
    const teams = currentUser.teams || []
    const shouldBeTreatedAsGlobal = getters.shouldBeTreatedAsGlobal
    const permissionsPerTeam = currentUser.permissions_per_team || {}

    if (shouldBeTreatedAsGlobal) {
      const globalTeam = getUserGlobalTeam(teams)
      if (globalTeam && permissionsPerTeam[globalTeam.id]) {
        return permissionsPerTeam[globalTeam.id][permission] || false
      }
    } else {
      if (permissionsPerTeam[teamId]) {
        return permissionsPerTeam[teamId][permission] || false
      }
    }

    return false
  },

  shouldBeTreatedAsGlobal: (_, __, ___, rootGetters) => {
    if (rootGetters[permissionEnabledGetter] === false) return true
    if (!rootGetters.currentUser?.permissions) return false

    const currentUser = rootGetters.currentUser
    const scopeIsGlobal = currentUser.scope_is_global
    const teams = currentUser.teams || []

    if (!scopeIsGlobal) return currentUser.active_team?.is_global || false

    const userGlobalTeam = getUserGlobalTeam(teams)

    return !!userGlobalTeam
  },

  getTeamsWithResourceAccess: (_, __, ___, rootGetters) => (resource, id) => {
    if (rootGetters[permissionEnabledGetter] === false) return true
    if (!rootGetters.currentUser?.permissions) return false
    if (!resource || !id) return []

    const currentUser = rootGetters.currentUser
    const teams = currentUser.teams || []

    const teamsWithAccess = teams.filter(team => {
      const resourceIds = resourceIdsFromTeamByResourceType(team, resource)
      return resourceIds.includes(id)
    }) || []

    if (teamsWithAccess.length > 0) return teamsWithAccess

    const userGlobalTeam = getUserGlobalTeam(teams)
    if (userGlobalTeam) return [userGlobalTeam]

    return []
  },
}

function resourceIdsFromTeamByResourceType (team, resource) {
  switch (resource) {
    case RESOURCETYPES.RECORDS:
      return team.permitted_records?.map(record => record.public_id) || []
    case RESOURCETYPES.PARTIES:
      return team.permitted_parties?.map(party => party.id) || []
    case RESOURCETYPES.LEGALCASES:
      return team.permitted_legal_cases?.map(legalCase => legalCase.id) || []
  }

  return []
}

function getUserGlobalTeam (teams) {
  return teams.find(team => team.is_global)
}
